import type { ThreadEntry } from "../../../api/threads/liveMessages";

/**
 * The part of one Thread's path the browser holds: entries `base + 1` to `lastNumber`, in
 * log order, with no gap and no repeat (threads.md T-1, T-15). New entries are appended
 * after the last number, from the live socket or an HTTP page; earlier ones are prepended
 * before `base` from HTTP pages. Pure functions; the live client keeps one per Thread.
 *
 * The server promises contiguous, once-only delivery. When it breaks that promise, nothing
 * out of order is shown: the entries that fit are kept, and the breach is recorded as a
 * violation, reported, and repaired by reading the missing range over HTTP.
 */
export interface ThreadLog {
  readonly threadId: string;
  /** The number of the last entry before the first one held; 0 when the log starts at 1. */
  readonly base: number;
  readonly entries: readonly ThreadEntry[];
  /** Breaches of the delivery promise seen for this Thread, oldest first, at most a few. */
  readonly violations: readonly LogViolation[];
}

export type LogViolation =
  /** An entry arrived after a number that is missing. */
  | { readonly kind: "gap"; readonly expectedNumber: number; readonly receivedNumber: number }
  /** An entry arrived again. `sameContent` is `null` when the first copy is not held. */
  | { readonly kind: "repeat"; readonly receivedNumber: number; readonly sameContent: boolean | null }
  /** An earlier page did not end right before the first entry held. */
  | { readonly kind: "misplacedPage"; readonly expectedLastNumber: number; readonly receivedLastNumber: number };

export interface LogUpdate {
  readonly log: ThreadLog;
  /** Violations found in this update; empty when everything fit. */
  readonly violations: readonly LogViolation[];
  /** A gap was found: entries after `lastNumberOf(log)` must be read again. */
  readonly needsRefill: boolean;
}

const KEPT_VIOLATIONS = 20;

export function emptyThreadLog(threadId: string, base: number): ThreadLog {
  if (!Number.isInteger(base) || base < 0) {
    throw new RangeError(`A Thread log starts after a non-negative entry number, not ${String(base)}.`);
  }
  return { threadId, base, entries: [], violations: [] };
}

/** The number of the last entry held, or `base` when none is. */
export function lastNumberOf(log: ThreadLog): number {
  return log.base + log.entries.length;
}

/** Whether the log holds the path from its first entry. */
export function holdsWholePath(log: ThreadLog): boolean {
  return log.base === 0;
}

export function entryAt(log: ThreadLog, number: number): ThreadEntry | undefined {
  return number > log.base ? log.entries[number - log.base - 1] : undefined;
}

/**
 * Append entries received after the last one held. Each must be the next number. A repeat
 * is skipped; a gap stops the update there and asks for a refill.
 */
export function appendEntries(log: ThreadLog, received: readonly ThreadEntry[]): LogUpdate {
  const appended: ThreadEntry[] = [];
  const violations: LogViolation[] = [];
  let expected = lastNumberOf(log) + 1;
  let needsRefill = false;

  for (const entry of received) {
    if (entry.number === expected) {
      appended.push(entry);
      expected += 1;
      continue;
    }
    if (entry.number < expected) {
      const held = entryAt(log, entry.number) ?? appended[entry.number - lastNumberOf(log) - 1];
      violations.push({ kind: "repeat", receivedNumber: entry.number, sameContent: held === undefined ? null : sameEntry(held, entry) });
      continue;
    }
    violations.push({ kind: "gap", expectedNumber: expected, receivedNumber: entry.number });
    needsRefill = true;
    break;
  }

  return {
    log: withViolations({ ...log, entries: appended.length === 0 ? log.entries : [...log.entries, ...appended] }, violations),
    violations,
    needsRefill,
  };
}

/**
 * Prepend a page of earlier entries, which must end right before the first one held. Only
 * the contiguous run that ends there is kept.
 */
export function prependEntries(log: ThreadLog, page: readonly ThreadEntry[]): LogUpdate {
  const last = page[page.length - 1];
  if (last === undefined) {
    return { log, violations: [], needsRefill: false };
  }
  if (last.number !== log.base) {
    const violation: LogViolation = { kind: "misplacedPage", expectedLastNumber: log.base, receivedLastNumber: last.number };
    return { log: withViolations(log, [violation]), violations: [violation], needsRefill: false };
  }
  const kept: ThreadEntry[] = [];
  const violations: LogViolation[] = [];
  let expected = log.base;
  for (let index = page.length - 1; index >= 0; index -= 1) {
    const entry = page[index];
    if (entry?.number !== expected) {
      if (entry !== undefined) {
        violations.push({ kind: "gap", expectedNumber: expected, receivedNumber: entry.number });
      }
      break;
    }
    kept.unshift(entry);
    expected -= 1;
  }
  return {
    log: withViolations({ ...log, base: log.base - kept.length, entries: [...kept, ...log.entries] }, violations),
    violations,
    needsRefill: false,
  };
}

/** The number to resume a subscription after: everything up to it is held. */
export function resumeAfter(log: ThreadLog): number {
  return lastNumberOf(log);
}

function withViolations(log: ThreadLog, violations: readonly LogViolation[]): ThreadLog {
  return violations.length === 0 ? log : { ...log, violations: [...log.violations, ...violations].slice(-KEPT_VIOLATIONS) };
}

function sameEntry(left: ThreadEntry, right: ThreadEntry): boolean {
  return left.thread_id === right.thread_id && left.at === right.at && JSON.stringify(left.payload) === JSON.stringify(right.payload);
}
